import type { AudioKernel } from "./abi";
import { clonePreset, DEFAULT_PRESETS } from "./defaultPresets";
import {
  clearFxChainState,
  patchFxChainState,
  processStereoFxChain,
  syncFxChainState,
  type FxChainState,
} from "./fxChain";
import {
  cloneDrumKit,
  cloneInstrument,
  createDefaultInstruments,
  drumPadIndexForNote,
  isDrumsInstrument,
  isSamplerInstrument,
  isSf2Instrument,
  isSynthInstrument,
  isWavetableInstrument,
  normalizeDrumKitMode,
  normalizeInstrument,
  DEFAULT_DRUM_SAMPLE_AMP_ENV,
} from "./drumKit";
import {
  normalizeWavetablePreset,
} from "../wavetable/preset";
import {
  createWtVoiceState,
  renderWavetableSample,
  type WtVoiceState,
} from "../wavetable/renderVoice";
import { getBasicWavetable } from "../wavetable/basicTables";
import {
  findSamplerZone,
  normalizeSamplerPreset,
} from "../sampler/preset";
import {
  advanceClassicSample,
  advanceGranularSample,
  classicRateFromCycle,
  classicRateFromRoot,
  createGranularState,
  dbToGain,
  resolveLoopXfade,
  type GranularState,
} from "../sampler/renderVoice";
import type { SamplerPreset, SamplerRuntimeSample, WavetablePreset, WavetableRuntimeTable } from "../types";
import {
  createDefaultSlotMixes,
  equalPowerPanGains,
  normalizeSlotMix,
  normalizeSlotMixes,
} from "./slotMix";
import {
  normalizeSlotFxStack,
  normalizeSlotFxStacks,
} from "./slotFx";
import { findSf2Zone } from "../sf2/findZone";
import type {
  Adsr,
  DrumKit,
  DrumPad,
  FxNode,
  Instrument,
  InstrumentSlot,
  KernelInitOptions,
  KernelMessage,
  ModRoute,
  Sf2Patch,
  Sf2RuntimeBank,
  SlotMix,
  SynthPreset,
  VoiceNoteFxParams,
  Waveform,
} from "../types";
import { MAX_INSTRUMENT_SLOT_COUNT } from "../types";
import {
  createEmptyTelemetry,
  METER_PUBLISH_SAMPLES,
  METER_SLOT_COUNT,
  type EngineTelemetry,
  type KernelTelemetryMessage,
} from "../telemetry";
import {
  advanceNoteFxPitch,
  createNoteFxState,
  patchNoteFxFromParams,
  resetNoteFxFromParams,
  type VoiceNoteFxState,
} from "./noteFxVoice";
import { MAX_FM_DEPTH, normalizePreset, type ResolvedSynthPreset } from "./presetDefaults";

type EnvStage = "idle" | "attack" | "decay" | "sustain" | "release";

type EnvelopeState = {
  stage: EnvStage;
  level: number;
  stageTime: number;
  releaseFrom: number;
};

type VoiceState = {
  active: boolean;
  note: number;
  velocity: number;
  /** Smoothed gain toward `velocity` (kills G-stutter clicks). */
  velocitySmooth: number;
  /** Smoothed pan toward noteFx.pan ?? slot pan (kills Bxx clicks). */
  panSmooth: number;
  freqHz: number;
  targetFreqHz: number;
  osc1Phase: number;
  osc2Phase: number;
  osc3Phase: number;
  ampEnv: EnvelopeState;
  modEnv: EnvelopeState;
  lfoPhase: number;
  filterZ1: number;
  filterZ2: number;
  noiseState: number;
  noteFx: VoiceNoteFxState;
};

/** Polyphonic one-shot voice for a drum pad hit. */
type DrumVoice = {
  active: boolean;
  age: number;
  /** MIDI note that triggered this voice (for targeted noteOff). */
  note: number;
  velocity: number;
  velocitySmooth: number;
  pad: DrumPad;
  /** Synth (tone/noise/click) or loaded sample oneshot. */
  playMode: "synth" | "sample";
  sampleId: string;
  sample: Float32Array | null;
  sampleRate: number;
  position: number;
  sampleStart: number;
  sampleEnd: number;
  playbackRate: number;
  phase: number;
  noiseState: number;
  filterZ1: number;
  timeSec: number;
  /** Sample-mode amp ADSR. Unused in synth mode (decayMs path). */
  ampEnv: EnvelopeState;
  /** Voice pan −1..1; null = inherit slot mix pan. */
  pan: number | null;
  /** Smoothed pan toward `pan ?? slot`. */
  panSmooth: number;
  noteFx: VoiceNoteFxState;
  /** Absolute pitch for slide / porta / vibrato / arp. */
  freqHz: number;
  targetFreqHz: number;
  refFreqHz: number;
};

const DRUM_VOICES_PER_SLOT = 16;
const SF2_VOICES_PER_SLOT = 16;
const SAMPLER_VOICES_PER_SLOT = 16;
const SF2_RELEASE_SEC = 0.08;
const SF2_FILTER_BASE_HZ = 8000;
/** Linear fade for gate/stutter velocity chops and pan moves (~4 ms @ 48 kHz). */
const GAIN_SMOOTH_SEC = 0.004;

/** Polyphonic sample voice for an SF2 slot. */
type Sf2Voice = {
  active: boolean;
  age: number;
  note: number;
  velocity: number;
  velocitySmooth: number;
  sample: Float32Array;
  position: number;
  /** Playback rate at note-on with zero pitch FX offset. */
  baseRate: number;
  rate: number;
  sampleStart: number;
  sampleEnd: number;
  loopStart: number;
  loopEnd: number;
  loopMode: number;
  gain: number;
  releasing: boolean;
  releaseEnv: number;
  /** Voice pan −1..1; null = inherit slot mix pan. */
  pan: number | null;
  /** Smoothed pan toward `pan ?? slot`. */
  panSmooth: number;
  noteFx: VoiceNoteFxState;
  freqHz: number;
  targetFreqHz: number;
  refFreqHz: number;
  filterZ1: number;
};

/** Polyphonic classic sampler voice. */
type SamplerVoice = {
  active: boolean;
  age: number;
  note: number;
  velocity: number;
  velocitySmooth: number;
  sampleId: string;
  sample: Float32Array;
  sampleRate: number;
  position: number;
  baseRate: number;
  rate: number;
  sampleStart: number;
  sampleEnd: number;
  loopEnabled: boolean;
  loopStart: number;
  loopEnd: number;
  /** Equal-power crossfade at loop splice (source frames). */
  loopXfade: number;
  reverse: boolean;
  oneCycle: boolean;
  gain: number;
  ampEnv: EnvelopeState;
  modEnv: EnvelopeState;
  lfoPhase: number;
  lfo2Phase: number;
  /** Stable 0..1 per note-on for `random` mod source. */
  noteRandom: number;
  baseSampleStart: number;
  baseLoopStart: number;
  baseLoopEnd: number;
  pan: number | null;
  panSmooth: number;
  noteFx: VoiceNoteFxState;
  freqHz: number;
  targetFreqHz: number;
  refFreqHz: number;
  filterZ1: number;
  filterZ2: number;
  granular: GranularState;
};

type TimedMessage = {
  time: number;
  message: KernelMessage;
};

function midiToHz(note: number): number {
  return 440 * 2 ** ((note - 69) / 12);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Advance smoothed value toward target (linear ramp — click-free gates / pan). */
function advanceLinearSmooth(current: number, target: number, dt: number): number {
  if (current === target) {
    return target;
  }
  const maxDelta = dt / GAIN_SMOOTH_SEC;
  const delta = target - current;
  if (Math.abs(delta) <= maxDelta) {
    return target;
  }
  return current + Math.sign(delta) * maxDelta;
}

function createEnvelope(): EnvelopeState {
  return { stage: "idle", level: 0, stageTime: 0, releaseFrom: 0 };
}

function createVoice(): VoiceState {
  return {
    active: false,
    note: 60,
    velocity: 0,
    velocitySmooth: 0,
    panSmooth: 0,
    freqHz: 440,
    targetFreqHz: 440,
    osc1Phase: 0,
    osc2Phase: 0,
    osc3Phase: 0,
    ampEnv: createEnvelope(),
    modEnv: createEnvelope(),
    lfoPhase: 0,
    filterZ1: 0,
    filterZ2: 0,
    noiseState: 1,
    noteFx: createNoteFxState(),
  };
}

function createDrumVoice(): DrumVoice {
  return {
    active: false,
    age: 0,
    note: 60,
    velocity: 0,
    velocitySmooth: 0,
    pad: {
      name: "",
      level: 0,
      pitchHz: 100,
      pitchEnv: 0,
      decayMs: 100,
      toneLevel: 0,
      noiseLevel: 0,
      clickLevel: 0,
      filterHz: 8000,
    },
    playMode: "synth",
    sampleId: "",
    sample: null,
    sampleRate: 48000,
    position: 0,
    sampleStart: 0,
    sampleEnd: 0,
    playbackRate: 1,
    phase: 0,
    noiseState: 1,
    filterZ1: 0,
    timeSec: 0,
    ampEnv: createEnvelope(),
    pan: null,
    panSmooth: 0,
    noteFx: createNoteFxState(),
    freqHz: 100,
    targetFreqHz: 100,
    refFreqHz: 100,
  };
}

function createSf2Voice(): Sf2Voice {
  return {
    active: false,
    age: 0,
    note: 60,
    velocity: 0,
    velocitySmooth: 0,
    sample: new Float32Array(0),
    position: 0,
    baseRate: 1,
    rate: 1,
    sampleStart: 0,
    sampleEnd: 0,
    loopStart: 0,
    loopEnd: 0,
    loopMode: 0,
    gain: 1,
    releasing: false,
    releaseEnv: 1,
    pan: null,
    panSmooth: 0,
    noteFx: createNoteFxState(),
    freqHz: 440,
    targetFreqHz: 440,
    refFreqHz: 440,
    filterZ1: 0,
  };
}

function createSamplerVoice(): SamplerVoice {
  return {
    active: false,
    age: 0,
    note: 60,
    velocity: 0,
    velocitySmooth: 0,
    sampleId: "",
    sample: new Float32Array(0),
    sampleRate: 48_000,
    position: 0,
    baseRate: 1,
    rate: 1,
    sampleStart: 0,
    sampleEnd: 0,
    loopEnabled: false,
    loopStart: 0,
    loopEnd: 0,
    loopXfade: 0,
    reverse: false,
    oneCycle: false,
    gain: 1,
    ampEnv: createEnvelope(),
    modEnv: createEnvelope(),
    lfoPhase: 0,
    lfo2Phase: 0,
    noteRandom: 0,
    baseSampleStart: 0,
    baseLoopStart: 0,
    baseLoopEnd: 0,
    pan: null,
    panSmooth: 0,
    noteFx: createNoteFxState(),
    freqHz: 440,
    targetFreqHz: 440,
    refFreqHz: 440,
    filterZ1: 0,
    filterZ2: 0,
    granular: createGranularState(8),
  };
}

function newestActiveVoice<T extends { active: boolean; age: number }>(
  pool: T[] | undefined,
): T | undefined {
  if (!pool) {
    return undefined;
  }
  let best: T | undefined;
  for (const voice of pool) {
    if (!voice.active) {
      continue;
    }
    if (!best || voice.age > best.age) {
      best = voice;
    }
  }
  return best;
}

function applyPanFromParams(
  voice: { pan: number | null },
  noteFx: VoiceNoteFxParams | undefined,
): void {
  if (noteFx?.pan !== undefined) {
    voice.pan = clamp(noteFx.pan, -1, 1);
  }
}

/**
 * Target pan for a voice (noteFx Bxx, else slot mix).
 * When `seedFrom` is set (prior hold still sounding), keep that as panSmooth so
 * advanceLinearSmooth ramps — snapping L↔R on C-- retriggers clicks hard.
 */
function applyVoicePanTarget(
  voice: { pan: number | null; panSmooth: number },
  noteFx: VoiceNoteFxParams | undefined,
  slotPan: number,
  seedFrom?: number,
): void {
  voice.pan = null;
  applyPanFromParams(voice, noteFx);
  const target = voice.pan ?? slotPan;
  voice.panSmooth = seedFrom !== undefined ? seedFrom : target;
}

function triggerEnvelope(env: EnvelopeState): void {
  env.stage = "attack";
  env.stageTime = 0;
  env.level = 0;
}

/**
 * Retrigger without an amp discontinuity: resume attack from the current level
 * so a mono steal does not slam gain to 0 mid-note.
 */
function softTriggerEnvelope(env: EnvelopeState, adsr: Adsr): void {
  const a = Math.max(adsr.a, 1e-4);
  const level = clamp(env.level, 0, 1);
  env.stage = "attack";
  env.stageTime = level * a;
  env.level = level;
}

function releaseEnvelope(env: EnvelopeState): void {
  if (env.stage === "idle") {
    return;
  }
  env.releaseFrom = env.level;
  env.stage = "release";
  env.stageTime = 0;
}

function advanceEnvelope(env: EnvelopeState, adsr: Adsr, dt: number): number {
  if (env.stage === "idle") {
    env.level = 0;
    return 0;
  }

  env.stageTime += dt;

  switch (env.stage) {
    case "attack": {
      const a = Math.max(adsr.a, 1e-4);
      env.level = clamp(env.stageTime / a, 0, 1);
      if (env.stageTime >= a) {
        env.stage = "decay";
        env.stageTime = 0;
        env.level = 1;
      }
      break;
    }
    case "decay": {
      const d = Math.max(adsr.d, 1e-4);
      const t = clamp(env.stageTime / d, 0, 1);
      env.level = 1 + (adsr.s - 1) * t;
      if (env.stageTime >= d) {
        env.stage = "sustain";
        env.stageTime = 0;
        env.level = adsr.s;
      }
      break;
    }
    case "sustain":
      env.level = adsr.s;
      break;
    case "release": {
      const r = Math.max(adsr.r, 1e-4);
      const t = clamp(env.stageTime / r, 0, 1);
      env.level = env.releaseFrom * (1 - t);
      if (env.stageTime >= r || env.level <= 1e-4) {
        env.stage = "idle";
        env.level = 0;
      }
      break;
    }
  }

  return env.level;
}

/**
 * PolyBLEP: the first-order correction for the alias residue a step
 * discontinuity leaves in a naively sampled waveform. `t` is the fractional
 * phase and `dt` the per-sample phase increment; the correction only bites in
 * the one-sample neighbourhood either side of the jump.
 */
function polyBlep(t: number, dt: number): number {
  if (dt <= 0) {
    return 0;
  }
  if (t < dt) {
    const x = t / dt;
    return x + x - x * x - 1;
  }
  if (t > 1 - dt) {
    const x = (t - 1) / dt;
    return x * x + x + x + 1;
  }
  return 0;
}

function noiseSample(noiseState: { value: number }): number {
  // xorshift-ish
  let x = noiseState.value | 0;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  noiseState.value = x;
  return (x >>> 0) / 0xffffffff * 2 - 1;
}

/**
 * `phaseInc` is the nominal per-sample phase step, used only to size the
 * PolyBLEP correction. Sine and triangle have no step discontinuity, so they
 * need none.
 */
function oscSample(
  wave: Waveform,
  phase: number,
  phaseInc: number,
  pwm: number,
  noiseState: { value: number },
): number {
  const p = phase - Math.floor(phase);
  const dt = Math.min(0.5, Math.abs(phaseInc));
  switch (wave) {
    case "sine":
      return Math.sin(2 * Math.PI * p);
    case "tri":
      return 1 - 4 * Math.abs(p - 0.5);
    case "saw":
      return 2 * p - 1 - polyBlep(p, dt);
    case "square": {
      const width = clamp(pwm, 0.05, 0.95);
      const naive = p < width ? 1 : -1;
      const fall = (p + 1 - width) % 1;
      return naive + polyBlep(p, dt) - polyBlep(fall, dt);
    }
    case "noise":
      return noiseSample(noiseState);
  }
}

function applyFilter(
  input: number,
  voice: VoiceState,
  type: SynthPreset["filter"]["type"],
  cutoffHz: number,
  resonance: number,
  sampleRate: number,
): number {
  // Chamberlin SVF blows up when f approaches/exceeds ~1 (fc near sr/6).
  // Cap fc and clamp f so Glass Bell / open Fxx rows cannot NaN the bus.
  const fc = clamp(cutoffHz, 20, sampleRate * 0.2);
  const f = Math.min(0.95, 2 * Math.sin(Math.PI * (fc / sampleRate)));
  const q = clamp(1 - resonance * 0.9, 0.1, 1);
  const low = voice.filterZ1 + f * voice.filterZ2;
  const high = input - low - q * voice.filterZ2;
  const band = voice.filterZ2 + f * high;
  if (!Number.isFinite(low) || !Number.isFinite(band) || !Number.isFinite(high)) {
    voice.filterZ1 = 0;
    voice.filterZ2 = 0;
    return 0;
  }
  voice.filterZ2 = clamp(band, -8, 8);
  voice.filterZ1 = clamp(low, -8, 8);
  switch (type) {
    case "lowpass":
      return voice.filterZ1;
    case "highpass":
      return clamp(high, -8, 8);
    case "bandpass":
      return voice.filterZ2;
  }
}

/** Blends dry carrier → ring modulation: 0.5 is classic AM, 1 is full ring mod. */
function ringMod(carrier: number, modulator: number, depth: number): number {
  return carrier * (1 - depth + depth * modulator);
}

function modAmount(
  routes: ModRoute[],
  source: ModRoute["source"],
  target: ModRoute["target"],
  value: number,
  polarity: "unipolar" | "bipolar" = "unipolar",
): number {
  let sum = 0;
  for (const route of routes) {
    if (route.source !== source || route.target !== target) {
      continue;
    }
    const v = route.invert
      ? (polarity === "bipolar" ? -value : 1 - value)
      : value;
    sum += v * route.depth;
  }
  return sum;
}

function samplerModAmount(
  routes: SamplerPreset["routes"],
  source: SamplerPreset["routes"][number]["source"],
  target: SamplerPreset["routes"][number]["target"],
  value: number,
  polarity: "unipolar" | "bipolar" = "unipolar",
): number {
  let sum = 0;
  for (const route of routes) {
    if (route.source !== source || route.target !== target) {
      continue;
    }
    const v = route.invert
      ? (polarity === "bipolar" ? -value : 1 - value)
      : value;
    sum += v * route.depth;
  }
  return sum;
}

function samplerLfoValue(wave: SamplerPreset["lfo"]["wave"], phase: number): number {
  const p = phase - Math.floor(phase);
  switch (wave) {
    case "tri":
      return 1 - 4 * Math.abs(p - 0.5);
    case "square":
      return p < 0.5 ? 1 : -1;
    case "saw":
      return 2 * p - 1;
    case "snh":
    case "random": {
      // Hold a deterministic pseudo-random per integer phase bucket.
      const bucket = Math.floor(phase);
      const x = Math.sin(bucket * 12.9898 + 78.233) * 43758.5453;
      return ((x - Math.floor(x)) * 2 - 1);
    }
    case "sine":
    default:
      return Math.sin(2 * Math.PI * p);
  }
}

/** Octaves above/below middle C (MIDI 60). Depth 1 on cutoff = 100% key track. */
function keyTrackOctaves(note: number): number {
  return (note - 60) / 12;
}

export class TsAudioKernel implements AudioKernel {
  sampleRate = 0;

  private playing = false;
  private bpm = 120;
  private transportStartTime = 0;
  /** Per-slot instrument (synth, drums, or sf2). */
  private instruments: Instrument[] = createDefaultInstruments();
  /** Synth voice presets mirrored for synth slots (normalized). */
  private presets: ResolvedSynthPreset[] = DEFAULT_PRESETS.map(normalizePreset);
  private kits: Array<DrumKit | null> = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => null);
  private sf2Patches: Array<Sf2Patch | null> = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => null);
  private sf2Banks = new Map<string, Sf2RuntimeBank>();
  private samplerPresets: Array<SamplerPreset | null> = Array.from(
    { length: MAX_INSTRUMENT_SLOT_COUNT },
    () => null,
  );
  private samplerSamples = new Map<string, SamplerRuntimeSample>();
  private samplerVoices: SamplerVoice[][] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () =>
    Array.from({ length: SAMPLER_VOICES_PER_SLOT }, () => createSamplerVoice()),
  );
  private samplerAgeCounter = 0;
  private wavetablePresets: Array<WavetablePreset | null> = Array.from(
    { length: MAX_INSTRUMENT_SLOT_COUNT },
    () => null,
  );
  private wavetableVoices: WtVoiceState[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () =>
    createWtVoiceState(),
  );
  private wavetableTables = new Map<string, WavetableRuntimeTable>();
  private slotMixes: SlotMix[] = createDefaultSlotMixes(MAX_INSTRUMENT_SLOT_COUNT);
  /** Stereo master FX (linked dual-mono for mono FX; true stereo for utility/colorReverb). */
  private fx: FxChainState = clearFxChainState();
  /** When true, master FX wet is ramped toward silence (stack stays warm). */
  private fxBypass = false;
  private fxBypassSmooth = 1;
  /** Stereo insert FX per slot (after volume, before strip pan). */
  private slotFx: FxChainState[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () =>
    clearFxChainState(),
  );
  private voices: VoiceState[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => createVoice());
  private drumVoices: DrumVoice[][] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () =>
    Array.from({ length: DRUM_VOICES_PER_SLOT }, () => createDrumVoice()),
  );
  private sf2Voices: Sf2Voice[][] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () =>
    Array.from({ length: SF2_VOICES_PER_SLOT }, () => createSf2Voice()),
  );
  private drumAgeCounter = 0;
  private sf2AgeCounter = 0;
  private pending: TimedMessage[] = [];
  private noiseScratch = { value: 1 };

  /** Per-slot peak / sum-of-squares accumulators for the current meter window. */
  private readonly meterPeakL = new Float32Array(MAX_INSTRUMENT_SLOT_COUNT);
  private readonly meterPeakR = new Float32Array(MAX_INSTRUMENT_SLOT_COUNT);
  private readonly meterSumSqL = new Float32Array(MAX_INSTRUMENT_SLOT_COUNT);
  private readonly meterSumSqR = new Float32Array(MAX_INSTRUMENT_SLOT_COUNT);
  private meterMasterPeakL = 0;
  private meterMasterPeakR = 0;
  private meterMasterSumSqL = 0;
  private meterMasterSumSqR = 0;
  private meterWindowSamples = 0;
  private meterPublishPending = false;
  private readonly telemetrySnapshot = createEmptyTelemetry(METER_SLOT_COUNT);
  private readonly telemetryWireData = new Float32Array(METER_SLOT_COUNT * 4);
  private readonly telemetryWireMaster = new Float32Array(4);

  init(options: KernelInitOptions): void {
    this.sampleRate = options.sampleRate;
  }

  handleMessage(message: KernelMessage): void {
    if (message.type === "init") {
      this.init({ sampleRate: message.sampleRate });
      return;
    }

    if (
      (message.type === "noteOn" || message.type === "noteOff" || message.type === "setNoteFx")
      && message.time > 0
    ) {
      this.pending.push({ time: message.time, message });
      this.pending.sort((a, b) => a.time - b.time);
      return;
    }

    this.applyMessage(message);
  }

  process(
    left: Float32Array,
    right: Float32Array,
    frames: number,
    currentTime: number,
  ): void {
    if (this.sampleRate <= 0) {
      left.fill(0, 0, frames);
      right.fill(0, 0, frames);
      return;
    }

    const dt = 1 / this.sampleRate;
    let pendingIndex = 0;

    for (let i = 0; i < frames; i++) {
      const t = currentTime + i * dt;
      while (pendingIndex < this.pending.length) {
        const event = this.pending[pendingIndex];
        if (!event || event.time > t) {
          break;
        }
        this.applyMessage(event.message);
        pendingIndex += 1;
      }

      let sumL = 0;
      let sumR = 0;
      for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
        const s = slot as InstrumentSlot;
        const mix = this.slotMixes[s] ?? { volume: 1, pan: 0 };
        const vol = mix.volume;
        let dryL = 0;
        let dryR = 0;
        let panTarget = mix.pan;
        let panSmoothRef: { panSmooth: number } | undefined;
        let hasEnergy = false;

        if (this.sf2Patches[s]) {
          const pool = this.sf2Voices[s];
          if (pool) {
            for (const voice of pool) {
              if (!voice.active) {
                continue;
              }
              const dry = this.renderSf2Voice(voice, dt);
              // Per-voice pan: poly pools must not share one slot panSmooth —
              // overlapping releases would steal Bxx from the next note.
              const panT = voice.pan ?? mix.pan;
              voice.panSmooth = advanceLinearSmooth(voice.panSmooth, panT, dt);
              const { left: gL, right: gR } = equalPowerPanGains(voice.panSmooth);
              dryL += dry * gL;
              dryR += dry * gR;
              hasEnergy = true;
            }
          }
        } else if (this.samplerPresets[s]) {
          const pool = this.samplerVoices[s];
          if (pool) {
            for (const voice of pool) {
              if (!voice.active) {
                continue;
              }
              const dry = this.renderSamplerVoice(s, voice, dt);
              const panT = voice.pan ?? mix.pan;
              voice.panSmooth = advanceLinearSmooth(voice.panSmooth, panT, dt);
              const { left: gL, right: gR } = equalPowerPanGains(voice.panSmooth);
              dryL += dry * gL;
              dryR += dry * gR;
              hasEnergy = true;
            }
          }
        } else if (this.kits[s]) {
          const pool = this.drumVoices[s];
          if (pool) {
            for (const voice of pool) {
              if (!voice.active) {
                continue;
              }
              const dry = this.renderDrumVoice(voice, dt);
              const panT = voice.pan ?? mix.pan;
              voice.panSmooth = advanceLinearSmooth(voice.panSmooth, panT, dt);
              const { left: gL, right: gR } = equalPowerPanGains(voice.panSmooth);
              dryL += dry * gL;
              dryR += dry * gR;
              hasEnergy = true;
            }
          }
        } else if (this.wavetablePresets[s]) {
          const stereo = this.renderWavetableSlot(s, dt);
          dryL += stereo.l;
          dryR += stereo.r;
          const wtVoice = this.wavetableVoices[s];
          if (wtVoice) {
            panTarget = wtVoice.noteFx.pan ?? mix.pan;
            panSmoothRef = wtVoice;
            if (wtVoice.active || stereo.l !== 0 || stereo.r !== 0) {
              hasEnergy = true;
            }
          }
        } else {
          const voice = this.voices[s];
          const dry = this.renderVoice(s, dt);
          dryL += dry;
          dryR += dry;
          if (voice) {
            panTarget = voice.noteFx.pan ?? mix.pan;
            panSmoothRef = voice;
            if (dry !== 0 || voice.active) {
              hasEnergy = true;
            }
          }
        }

        const insert = this.slotFx[s];
        const hasInsert = !!insert && insert.nodes.length > 0;
        if (!hasInsert && !hasEnergy && dryL === 0 && dryR === 0) {
          continue;
        }

        let outL: number;
        let outR: number;
        if (hasInsert) {
          const wet = processStereoFxChain(dryL * vol, dryR * vol, insert, this.sampleRate);
          outL = wet.l;
          outR = wet.r;
        } else {
          outL = dryL * vol;
          outR = dryR * vol;
        }

        // Mono synth/wavetable: apply slot pan after inserts. Poly pools already panned.
        if (panSmoothRef) {
          panSmoothRef.panSmooth = advanceLinearSmooth(panSmoothRef.panSmooth, panTarget, dt);
          const { left: gL, right: gR } = equalPowerPanGains(panSmoothRef.panSmooth);
          const contribL = outL * gL;
          const contribR = outR * gR;
          sumL += contribL;
          sumR += contribR;
          this.accumulateSlotMeter(s, contribL, contribR);
        } else {
          sumL += outL;
          sumR += outR;
          this.accumulateSlotMeter(s, outL, outR);
        }
      }

      {
        const dryL = sumL * 0.35;
        const dryR = sumR * 0.35;
        const master = processStereoFxChain(dryL, dryR, this.fx, this.sampleRate);
        this.fxBypassSmooth = advanceLinearSmooth(
          this.fxBypassSmooth,
          this.fxBypass ? 0 : 1,
          dt,
        );
        const g = this.fxBypassSmooth;
        sumL = dryL * (1 - g) + master.l * g;
        sumR = dryR * (1 - g) + master.r * g;
      }
      const outSampleL = Number.isFinite(sumL) ? clamp(sumL, -1, 1) : 0;
      const outSampleR = Number.isFinite(sumR) ? clamp(sumR, -1, 1) : 0;
      left[i] = outSampleL;
      right[i] = outSampleR;
      this.accumulateMasterMeter(outSampleL, outSampleR);
      this.meterWindowSamples += 1;
      if (this.meterWindowSamples >= METER_PUBLISH_SAMPLES) {
        this.publishMeters();
      }
    }

    if (pendingIndex > 0) {
      this.pending = this.pending.slice(pendingIndex);
    }
  }

  getTelemetry(): EngineTelemetry {
    return this.telemetrySnapshot;
  }

  takeTelemetryMessage(): KernelTelemetryMessage | null {
    if (!this.meterPublishPending) {
      return null;
    }
    this.meterPublishPending = false;
    const tracks = this.telemetrySnapshot.tracks;
    const data = this.telemetryWireData;
    for (let i = 0; i < METER_SLOT_COUNT; i++) {
      const t = tracks[i]!;
      const o = i * 4;
      data[o] = t.rmsL;
      data[o + 1] = t.rmsR;
      data[o + 2] = t.peakL;
      data[o + 3] = t.peakR;
    }
    const master = this.telemetrySnapshot.master;
    this.telemetryWireMaster[0] = master.rmsL;
    this.telemetryWireMaster[1] = master.rmsR;
    this.telemetryWireMaster[2] = master.peakL;
    this.telemetryWireMaster[3] = master.peakR;
    return {
      type: "telemetry",
      n: METER_SLOT_COUNT,
      data,
      master: this.telemetryWireMaster,
    };
  }

  private accumulateSlotMeter(slot: number, l: number, r: number): void {
    const aL = l < 0 ? -l : l;
    const aR = r < 0 ? -r : r;
    if (aL > this.meterPeakL[slot]!) {
      this.meterPeakL[slot] = aL;
    }
    if (aR > this.meterPeakR[slot]!) {
      this.meterPeakR[slot] = aR;
    }
    this.meterSumSqL[slot]! += l * l;
    this.meterSumSqR[slot]! += r * r;
  }

  private accumulateMasterMeter(l: number, r: number): void {
    const aL = l < 0 ? -l : l;
    const aR = r < 0 ? -r : r;
    if (aL > this.meterMasterPeakL) {
      this.meterMasterPeakL = aL;
    }
    if (aR > this.meterMasterPeakR) {
      this.meterMasterPeakR = aR;
    }
    this.meterMasterSumSqL += l * l;
    this.meterMasterSumSqR += r * r;
  }

  private publishMeters(): void {
    const n = Math.max(1, this.meterWindowSamples);
    const inv = 1 / n;
    const tracks = this.telemetrySnapshot.tracks;
    for (let i = 0; i < METER_SLOT_COUNT; i++) {
      const track = tracks[i]!;
      track.peakL = this.meterPeakL[i]!;
      track.peakR = this.meterPeakR[i]!;
      track.rmsL = Math.sqrt(this.meterSumSqL[i]! * inv);
      track.rmsR = Math.sqrt(this.meterSumSqR[i]! * inv);
      this.meterPeakL[i] = 0;
      this.meterPeakR[i] = 0;
      this.meterSumSqL[i] = 0;
      this.meterSumSqR[i] = 0;
    }
    // Clear unused high slots so peaks don't stick if METER_SLOT_COUNT grows later.
    for (let i = METER_SLOT_COUNT; i < MAX_INSTRUMENT_SLOT_COUNT; i++) {
      this.meterPeakL[i] = 0;
      this.meterPeakR[i] = 0;
      this.meterSumSqL[i] = 0;
      this.meterSumSqR[i] = 0;
    }
    const master = this.telemetrySnapshot.master;
    master.peakL = this.meterMasterPeakL;
    master.peakR = this.meterMasterPeakR;
    master.rmsL = Math.sqrt(this.meterMasterSumSqL * inv);
    master.rmsR = Math.sqrt(this.meterMasterSumSqR * inv);
    this.meterMasterPeakL = 0;
    this.meterMasterPeakR = 0;
    this.meterMasterSumSqL = 0;
    this.meterMasterSumSqR = 0;
    this.meterWindowSamples = 0;
    this.meterPublishPending = true;
  }

  private applyMessage(message: KernelMessage): void {
    switch (message.type) {
      case "init":
        this.init({ sampleRate: message.sampleRate });
        break;
      case "setBpm":
        this.bpm = message.bpm;
        break;
      case "transport":
        this.playing = message.playing;
        this.transportStartTime = message.startTime;
        // Note: stop does not allNotesOff — hosts silence explicitly so
        // shared multi-player sessions can end a one-shot without killing others.
        break;
      case "setInstrument": {
        const instrument = normalizeInstrument(message.instrument, message.slot);
        this.instruments[message.slot] = cloneInstrument(instrument);
        if (isSynthInstrument(instrument)) {
          this.presets[message.slot] = normalizePreset(clonePreset(instrument.preset));
          this.kits[message.slot] = null;
          this.sf2Patches[message.slot] = null;
          this.samplerPresets[message.slot] = null;
          this.wavetablePresets[message.slot] = null;
          this.releaseDrumSlot(message.slot);
          this.releaseSf2Slot(message.slot);
          this.releaseSamplerSlot(message.slot);
          this.releaseWavetableSlot(message.slot);
        } else if (isDrumsInstrument(instrument)) {
          this.kits[message.slot] = cloneDrumKit(instrument.kit);
          this.sf2Patches[message.slot] = null;
          this.samplerPresets[message.slot] = null;
          this.wavetablePresets[message.slot] = null;
          this.releaseSf2Slot(message.slot);
          this.releaseSamplerSlot(message.slot);
          this.releaseWavetableSlot(message.slot);
          this.noteOff(message.slot);
        } else if (isWavetableInstrument(instrument)) {
          const wasWavetable = this.wavetablePresets[message.slot] != null;
          this.kits[message.slot] = null;
          this.sf2Patches[message.slot] = null;
          this.samplerPresets[message.slot] = null;
          this.releaseDrumSlot(message.slot);
          this.releaseSf2Slot(message.slot);
          this.releaseSamplerSlot(message.slot);
          this.wavetablePresets[message.slot] = normalizeWavetablePreset(instrument.preset);
          // Live preset tweaks must not cut held notes — only silence when
          // switching into wavetable from another kind.
          if (!wasWavetable) {
            this.noteOff(message.slot);
          }
        } else if (isSamplerInstrument(instrument)) {
          const wasSampler = this.samplerPresets[message.slot] != null;
          this.kits[message.slot] = null;
          this.sf2Patches[message.slot] = null;
          this.wavetablePresets[message.slot] = null;
          this.releaseDrumSlot(message.slot);
          this.releaseSf2Slot(message.slot);
          this.releaseWavetableSlot(message.slot);
          this.samplerPresets[message.slot] = normalizeSamplerPreset(instrument.preset);
          // Live preset tweaks must not cut held notes — only silence when
          // switching into sampler from another kind.
          if (!wasSampler) {
            this.noteOff(message.slot);
          }
        } else if (isSf2Instrument(instrument)) {
          const wasSf2 = this.sf2Patches[message.slot] != null;
          this.kits[message.slot] = null;
          this.samplerPresets[message.slot] = null;
          this.wavetablePresets[message.slot] = null;
          this.releaseDrumSlot(message.slot);
          this.releaseSamplerSlot(message.slot);
          this.releaseWavetableSlot(message.slot);
          if (!wasSf2) {
            const voice = this.voices[message.slot];
            if (voice?.active) {
              releaseEnvelope(voice.ampEnv);
              releaseEnvelope(voice.modEnv);
            }
          }
          this.sf2Patches[message.slot] = { ...instrument.patch };
        } else if (instrument.kind === "externalMidi") {
          this.kits[message.slot] = null;
          this.sf2Patches[message.slot] = null;
          this.samplerPresets[message.slot] = null;
          this.wavetablePresets[message.slot] = null;
          this.releaseDrumSlot(message.slot);
          this.releaseSf2Slot(message.slot);
          this.releaseSamplerSlot(message.slot);
          this.releaseWavetableSlot(message.slot);
          this.noteOff(message.slot);
        }
        break;
      }
      case "loadSf2Bank": {
        this.sf2Banks.set(message.fontId, message.bank);
        break;
      }
      case "loadWavetable": {
        this.wavetableTables.set(message.table.id, message.table);
        break;
      }
      case "loadSample": {
        this.samplerSamples.set(message.sample.id, message.sample);
        break;
      }
      case "setSlotMix": {
        this.slotMixes[message.slot] = normalizeSlotMix(message.mix);
        break;
      }
      case "setSlotMixes": {
        // Pad/truncate to full capacity (same as setSlotFxStacks) so a short
        // payload cannot silently shrink the strip and drop slots 9+.
        this.slotMixes = normalizeSlotMixes(message.mixes, MAX_INSTRUMENT_SLOT_COUNT);
        break;
      }
      case "setFxStack": {
        const sr = this.sampleRate || 48000;
        syncFxChainState(this.fx, message.effects, sr);
        break;
      }
      case "setFx": {
        const sr = this.sampleRate || 48000;
        patchFxChainState(this.fx, message.index, message.patch, sr);
        break;
      }
      case "setFxBypass": {
        this.fxBypass = message.bypass === true;
        break;
      }
      case "clearFx":
        this.fx = clearFxChainState();
        break;
      case "setSlotFxStack": {
        const sr = this.sampleRate || 48000;
        const effects = normalizeSlotFxStack(message.effects);
        const existing = this.slotFx[message.slot] ?? clearFxChainState();
        syncFxChainState(existing, effects, sr);
        this.slotFx[message.slot] = existing;
        break;
      }
      case "setSlotFxStacks": {
        const sr = this.sampleRate || 48000;
        const stacks = normalizeSlotFxStacks(message.stacks, MAX_INSTRUMENT_SLOT_COUNT);
        for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
          const existing = this.slotFx[slot] ?? clearFxChainState();
          syncFxChainState(existing, stacks[slot] ?? [], sr);
          this.slotFx[slot] = existing;
        }
        break;
      }
      case "clearSlotFx": {
        if (message.slot !== undefined) {
          this.slotFx[message.slot] = clearFxChainState();
        } else {
          this.slotFx = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => clearFxChainState());
        }
        break;
      }
      case "noteOn":
        this.noteOn(message.slot, message.note, message.velocity, message.noteFx);
        break;
      case "noteOff":
        this.noteOff(message.slot, message.note);
        break;
      case "setNoteFx":
        this.setNoteFx(message.slot, message.noteFx, message.velocity);
        break;
      case "allNotesOff":
        this.allNotesOff();
        break;
      case "cancelPending":
        this.cancelPending(message.afterTime);
        break;
    }
  }

  /** Drop future timed events; voices already sounding keep playing. */
  private cancelPending(afterTime: number): void {
    this.pending = this.pending.filter(event => event.time <= afterTime);
  }

  private noteOn(
    slot: InstrumentSlot,
    note: number,
    velocity: number,
    noteFx?: VoiceNoteFxParams,
  ): void {
    // External MIDI slots stay silent in the audio kernel.
    if (this.instruments[slot]?.kind === "externalMidi") {
      return;
    }
    const sf2 = this.sf2Patches[slot];
    if (sf2) {
      this.sf2NoteOn(slot, sf2, note, velocity, noteFx);
      return;
    }
    if (this.samplerPresets[slot]) {
      this.samplerNoteOn(slot, note, velocity, noteFx);
      return;
    }
    const kit = this.kits[slot];
    if (kit) {
      this.drumNoteOn(slot, kit, note, velocity, noteFx);
      return;
    }
    if (this.wavetablePresets[slot]) {
      this.wavetableNoteOn(slot, note, velocity, noteFx);
      return;
    }

    const voice = this.voices[slot];
    const preset = this.presets[slot];
    if (!voice || !preset) {
      return;
    }
    const wasActive = voice.ampEnv.stage !== "idle";
    const porta = noteFx?.portaSemiPerSec ?? 0;
    const hardRetrigger = noteFx?.hardRetrigger === true;
    voice.active = true;
    voice.note = note;
    voice.velocity = clamp(velocity, 0, 1);
    voice.velocitySmooth = voice.velocity;
    const base = midiToHz(note);
    voice.targetFreqHz = base;
    resetNoteFxFromParams(voice.noteFx, noteFx);
    // Keep panSmooth when stealing so Bxx ramps; snap only on a silent start.
    if (!wasActive) {
      voice.panSmooth = voice.noteFx.pan ?? (this.slotMixes[slot]?.pan ?? 0);
    }

    // Portamento: keep current pitch and glide toward the new note (no env retrigger).
    if (wasActive && porta > 0 && !hardRetrigger) {
      voice.noteFx.portaSemiPerSec = porta;
      return;
    }

    // Snap pitch unless an active voice is gliding toward the new note.
    if (!wasActive || hardRetrigger || !preset.glideMs || preset.glideMs <= 0) {
      voice.freqHz = base;
    }
    if (!wasActive || hardRetrigger) {
      // Fresh voice, or tracker ratchet: hard attack from zero (phases too —
      // soft mid-note restarts would just “squish” sustain level).
      voice.osc1Phase = 0;
      voice.osc2Phase = 0;
      voice.osc3Phase = 0;
      triggerEnvelope(voice.ampEnv);
      triggerEnvelope(voice.modEnv);
    } else {
      softTriggerEnvelope(voice.ampEnv, preset.ampEnv);
      softTriggerEnvelope(voice.modEnv, preset.modEnv);
    }
  }

  private wavetableNoteOn(
    slot: InstrumentSlot,
    note: number,
    velocity: number,
    noteFx?: VoiceNoteFxParams,
  ): void {
    const voice = this.wavetableVoices[slot];
    const preset = this.wavetablePresets[slot];
    if (!voice || !preset) {
      return;
    }
    const wasActive = voice.ampEnv.stage !== "idle";
    const porta = noteFx?.portaSemiPerSec ?? 0;
    const hardRetrigger = noteFx?.hardRetrigger === true;
    voice.active = true;
    voice.note = note;
    voice.velocity = clamp(velocity, 0, 1);
    voice.velocitySmooth = voice.velocity;
    const base = midiToHz(note);
    voice.targetFreqHz = base;
    resetNoteFxFromParams(voice.noteFx, noteFx);
    if (!wasActive) {
      voice.panSmooth = voice.noteFx.pan ?? (this.slotMixes[slot]?.pan ?? 0);
    }

    if (wasActive && porta > 0 && !hardRetrigger) {
      voice.noteFx.portaSemiPerSec = porta;
      return;
    }

    if (!wasActive || hardRetrigger || !preset.glideMs || preset.glideMs <= 0) {
      voice.freqHz = base;
    }
    if (!wasActive || hardRetrigger) {
      for (const u of voice.unison) {
        u.phase1 = 0;
        u.phase2 = 0;
      }
      voice.subPhase = 0;
      triggerEnvelope(voice.ampEnv as EnvelopeState);
      triggerEnvelope(voice.modEnv as EnvelopeState);
    } else {
      softTriggerEnvelope(voice.ampEnv as EnvelopeState, preset.ampEnv);
      softTriggerEnvelope(voice.modEnv as EnvelopeState, preset.modEnv);
    }
  }

  private setNoteFx(
    slot: InstrumentSlot,
    noteFx: VoiceNoteFxParams,
    velocity?: number,
  ): void {
    if (this.sf2Patches[slot]) {
      const voice = newestActiveVoice(this.sf2Voices[slot]);
      if (!voice) {
        return;
      }
      patchNoteFxFromParams(voice.noteFx, noteFx);
      applyPanFromParams(voice, noteFx);
      if (velocity !== undefined) {
        voice.velocity = clamp(velocity, 0, 1);
      }
      return;
    }
    if (this.samplerPresets[slot]) {
      const voice = newestActiveVoice(this.samplerVoices[slot]);
      if (!voice) {
        return;
      }
      patchNoteFxFromParams(voice.noteFx, noteFx);
      applyPanFromParams(voice, noteFx);
      if (velocity !== undefined) {
        voice.velocity = clamp(velocity, 0, 1);
      }
      return;
    }
    if (this.kits[slot]) {
      const voice = newestActiveVoice(this.drumVoices[slot]);
      if (!voice) {
        return;
      }
      patchNoteFxFromParams(voice.noteFx, noteFx);
      applyPanFromParams(voice, noteFx);
      if (velocity !== undefined) {
        voice.velocity = clamp(velocity, 0, 1);
      }
      return;
    }
    if (this.wavetablePresets[slot]) {
      const voice = this.wavetableVoices[slot];
      if (!voice || !voice.active) {
        return;
      }
      patchNoteFxFromParams(voice.noteFx, noteFx);
      if (velocity !== undefined) {
        voice.velocity = clamp(velocity, 0, 1);
      }
      return;
    }
    const voice = this.voices[slot];
    if (!voice || !voice.active) {
      return;
    }
    patchNoteFxFromParams(voice.noteFx, noteFx);
    if (velocity !== undefined) {
      voice.velocity = clamp(velocity, 0, 1);
    }
  }

  private noteOff(slot: InstrumentSlot, note?: number): void {
    const kit = this.kits[slot];
    if (kit) {
      // Synth drums stay fire-and-forget. Sample drums release on Cxx / ---.
      if (normalizeDrumKitMode(kit.mode) !== "sample") {
        return;
      }
      for (const voice of this.drumVoices[slot] ?? []) {
        if (!voice.active || voice.playMode !== "sample") {
          continue;
        }
        if (note !== undefined && voice.note !== note) {
          continue;
        }
        releaseEnvelope(voice.ampEnv);
      }
      return;
    }
    if (this.sf2Patches[slot]) {
      this.sf2NoteOff(slot, note);
      return;
    }
    if (this.samplerPresets[slot]) {
      this.samplerNoteOff(slot, note);
      return;
    }
    if (this.wavetablePresets[slot]) {
      const wt = this.wavetableVoices[slot];
      if (!wt || !wt.active) {
        return;
      }
      releaseEnvelope(wt.ampEnv as EnvelopeState);
      releaseEnvelope(wt.modEnv as EnvelopeState);
      return;
    }
    const voice = this.voices[slot];
    if (!voice || !voice.active) {
      return;
    }
    releaseEnvelope(voice.ampEnv);
    releaseEnvelope(voice.modEnv);
  }

  private allNotesOff(): void {
    this.pending = [];
    for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
      this.noteOff(slot as InstrumentSlot);
      this.releaseDrumSlot(slot as InstrumentSlot);
      this.releaseSf2Slot(slot as InstrumentSlot);
      this.releaseSamplerSlot(slot as InstrumentSlot);
      this.releaseWavetableSlot(slot as InstrumentSlot);
    }
  }

  private releaseDrumSlot(slot: InstrumentSlot): void {
    for (const voice of this.drumVoices[slot] ?? []) {
      voice.active = false;
      voice.ampEnv.stage = "idle";
      voice.ampEnv.level = 0;
    }
  }

  private releaseSf2Slot(slot: InstrumentSlot): void {
    for (const voice of this.sf2Voices[slot] ?? []) {
      voice.active = false;
      voice.releasing = false;
    }
  }

  private releaseSamplerSlot(slot: InstrumentSlot): void {
    for (const voice of this.samplerVoices[slot] ?? []) {
      voice.active = false;
      voice.ampEnv.stage = "idle";
      voice.ampEnv.level = 0;
      voice.modEnv.stage = "idle";
      voice.modEnv.level = 0;
    }
  }

  private releaseWavetableSlot(slot: InstrumentSlot): void {
    const voice = this.wavetableVoices[slot];
    if (!voice) {
      return;
    }
    voice.active = false;
    voice.ampEnv.stage = "idle";
    voice.ampEnv.level = 0;
    voice.modEnv.stage = "idle";
    voice.modEnv.level = 0;
  }

  private getWavetableTable(id: string): WavetableRuntimeTable | undefined {
    return this.wavetableTables.get(id) ?? getBasicWavetable(id);
  }

  private renderWavetableSlot(slot: InstrumentSlot, dt: number): { l: number; r: number } {
    const voice = this.wavetableVoices[slot];
    const preset = this.wavetablePresets[slot];
    if (!voice || !preset || voice.ampEnv.stage === "idle") {
      if (voice) {
        voice.active = false;
      }
      return { l: 0, r: 0 };
    }

    const amp = advanceEnvelope(voice.ampEnv as EnvelopeState, preset.ampEnv, dt);
    const mod = advanceEnvelope(voice.modEnv as EnvelopeState, preset.modEnv, dt);
    const pitchModSemi = advanceNoteFxPitch(
      voice.noteFx,
      voice,
      dt,
      preset.glideMs,
    );

    return renderWavetableSample({
      sampleRate: this.sampleRate,
      dt,
      preset,
      voice,
      getTable: id => this.getWavetableTable(id),
      amp,
      mod,
      pitchModSemi,
      cutoffOctExtra: voice.noteFx.cutoffOct,
    });
  }

  private samplerNoteOn(
    slot: InstrumentSlot,
    note: number,
    velocity: number,
    noteFx?: VoiceNoteFxParams,
  ): void {
    const preset = this.samplerPresets[slot];
    if (!preset) {
      return;
    }
    const velMidi = Math.round(clamp(velocity, 0, 1) * 127);
    const zone = findSamplerZone(preset, note, velMidi);
    if (!zone) {
      return;
    }
    const runtime = this.samplerSamples.get(zone.sampleId);
    if (!runtime || runtime.pcm.length === 0) {
      return;
    }

    const pool = this.samplerVoices[slot]!;
    const maxVoices = Math.min(SAMPLER_VOICES_PER_SLOT, preset.maxVoices ?? SAMPLER_VOICES_PER_SLOT);
    const activePool = pool.slice(0, maxVoices);

    const porta = noteFx?.portaSemiPerSec ?? 0;
    const hardRetrigger = noteFx?.hardRetrigger === true;
    const targetHz = midiToHz(note);

    if (porta > 0 && !hardRetrigger) {
      const legato = activePool.find(v => v.active && v.ampEnv.stage !== "idle" && v.ampEnv.stage !== "release");
      if (legato) {
        this.samplerAgeCounter += 1;
        legato.age = this.samplerAgeCounter;
        legato.note = Math.round(note);
        legato.velocity = clamp(velocity, 0, 1);
        legato.velocitySmooth = legato.velocity;
        legato.targetFreqHz = targetHz;
        resetNoteFxFromParams(legato.noteFx, noteFx);
        legato.noteFx.portaSemiPerSec = porta;
        applyPanFromParams(legato, noteFx);
        // Keep legato.panSmooth — mix loop ramps toward legato.pan.
        // Recompute rate for new pitch relative to root.
        const tune = zone.tuneCents ?? 0;
        legato.baseRate = runtime.oneCycle
          ? classicRateFromCycle(runtime.pcm.length, this.sampleRate, note, tune)
          : classicRateFromRoot(runtime.sampleRate, this.sampleRate, note, zone.rootKey, tune);
        legato.rate = legato.baseRate;
        legato.refFreqHz = targetHz;
        return;
      }
    }

    // Tracker slots are mono (like synth / SF2): a new note cuts previous holds.
    // Chord polyphony is done with multiple tracks, not voices in one slot.
    // C-- relies on this — PatternPlayer does not gate noteOff before the next note.
    // Seed new-voice panSmooth from the newest hold so Bxx ramps (no L↔R snap).
    let panSeed: number | undefined;
    {
      let newestAge = -1;
      for (const prev of activePool) {
        if (!prev.active || prev.ampEnv.stage === "idle") {
          continue;
        }
        if (prev.age >= newestAge) {
          newestAge = prev.age;
          panSeed = prev.panSmooth;
        }
      }
    }
    for (const prev of activePool) {
      if (!prev.active || prev.ampEnv.stage === "idle") {
        continue;
      }
      if (prev.ampEnv.stage !== "release") {
        releaseEnvelope(prev.ampEnv);
        releaseEnvelope(prev.modEnv);
      }
      // Duck outgoing sample through the shared gain smooth (~4 ms) so rapid
      // keyboard / tracker retriggers don't hard-cut mid-waveform.
      prev.velocity = 0;
      // Continuous loops keep ringing into release/amp duck; until-release exits.
      if (preset.loop.mode !== "continuous") {
        prev.loopEnabled = false;
      }
    }

    let voice = activePool.find(v => !v.active);
    if (!voice) {
      voice = activePool.find(v => v.ampEnv.stage === "release")
        ?? activePool.reduce((oldest, v) => (v.age < oldest.age ? v : oldest));
    }

    const pcmLen = runtime.pcm.length;
    const regionStart = zone.region?.start
      ?? (preset.region.end > preset.region.start ? preset.region.start : 0);
    const regionEnd = zone.region?.end
      ?? (preset.region.end > preset.region.start ? preset.region.end : pcmLen);
    const sampleStart = Math.max(0, Math.min(Math.round(regionStart), pcmLen - 1));
    const sampleEnd = Math.max(sampleStart + 1, Math.min(Math.round(regionEnd), pcmLen));

    let loopStart = preset.loop.start > 0 || preset.loop.end > 0
      ? Math.round(preset.loop.start)
      : sampleStart;
    let loopEnd = preset.loop.end > preset.loop.start
      ? Math.round(preset.loop.end)
      : sampleEnd;
    loopStart = Math.max(sampleStart, Math.min(loopStart, sampleEnd - 1));
    loopEnd = Math.max(loopStart + 1, Math.min(loopEnd, sampleEnd));

    const tune = zone.tuneCents ?? 0;
    const baseRate = runtime.oneCycle
      ? classicRateFromCycle(pcmLen, this.sampleRate, note, tune)
      : classicRateFromRoot(runtime.sampleRate, this.sampleRate, note, zone.rootKey, tune);

    this.samplerAgeCounter += 1;
    voice.active = true;
    voice.age = this.samplerAgeCounter;
    voice.note = Math.round(note);
    voice.velocity = clamp(velocity, 0, 1);
    // Soft attack: ramp from silence so sample starts / retriggers aren't clicks
    // when the region doesn't begin (or resume) at a matching zero-crossing.
    voice.velocitySmooth = 0;
    voice.sampleId = runtime.id;
    voice.sample = runtime.pcm;
    voice.sampleRate = runtime.sampleRate;
    voice.oneCycle = runtime.oneCycle === true;
    voice.sampleStart = sampleStart;
    voice.sampleEnd = sampleEnd;
    voice.loopEnabled = preset.loop.enabled;
    voice.loopStart = loopStart;
    voice.loopEnd = loopEnd;
    voice.loopXfade = preset.loop.enabled && !runtime.oneCycle
      ? resolveLoopXfade(preset.loop.xfade, loopEnd - loopStart, runtime.sampleRate)
      : 0;
    voice.baseSampleStart = sampleStart;
    voice.baseLoopStart = loopStart;
    voice.baseLoopEnd = loopEnd;
    voice.reverse = zone.reverse === true || preset.reverse === true;
    voice.position = voice.reverse ? sampleEnd - 1e-6 : sampleStart;
    voice.baseRate = baseRate;
    voice.rate = baseRate;
    voice.gain = clamp(dbToGain(zone.gainDb ?? 0), 0, 4);
    voice.filterZ1 = 0;
    voice.filterZ2 = 0;
    voice.lfoPhase = 0;
    voice.lfo2Phase = 0;
    voice.noteRandom = Math.random();
    voice.granular = createGranularState(8);
    voice.granular.head = sampleStart;
    // Note-on startOffset from velocity / random / static routes (depth as 0..1 of region).
    {
      const vel = voice.velocity;
      const startMod =
        samplerModAmount(preset.routes, "velocity", "startOffset", vel)
        + samplerModAmount(preset.routes, "random", "startOffset", voice.noteRandom);
      if (startMod !== 0) {
        const span = Math.max(1, sampleEnd - sampleStart);
        const offset = clamp(startMod, -1, 1) * span;
        if (voice.reverse) {
          voice.position = clamp(voice.position - offset, sampleStart, sampleEnd - 1e-6);
        } else {
          voice.position = clamp(voice.position + offset, sampleStart, sampleEnd - 1e-6);
        }
      }
    }
    voice.refFreqHz = targetHz;
    voice.freqHz = targetHz;
    voice.targetFreqHz = targetHz;
    resetNoteFxFromParams(voice.noteFx, noteFx);
    applyVoicePanTarget(voice, noteFx, this.slotMixes[slot]?.pan ?? 0, panSeed);
    triggerEnvelope(voice.ampEnv);
    triggerEnvelope(voice.modEnv);
  }

  private samplerNoteOff(slot: InstrumentSlot, note?: number): void {
    const preset = this.samplerPresets[slot];
    const exitLoopOnRelease = preset?.loop.mode !== "continuous";
    const target = note === undefined ? undefined : Math.round(note);
    for (const voice of this.samplerVoices[slot] ?? []) {
      if (!voice.active || voice.ampEnv.stage === "idle" || voice.ampEnv.stage === "release") {
        continue;
      }
      if (target !== undefined && voice.note !== target) {
        continue;
      }
      releaseEnvelope(voice.ampEnv);
      releaseEnvelope(voice.modEnv);
      // Until-release (SF2): stop looping so the post-loop tail can play.
      // Continuous: keep looping while amp release fades (one-cycles / pads).
      if (exitLoopOnRelease && voice.loopEnabled) {
        voice.loopEnabled = false;
      }
    }
  }

  private renderSamplerVoice(slot: InstrumentSlot, voice: SamplerVoice, dt: number): number {
    const preset = this.samplerPresets[slot];
    if (!preset || voice.ampEnv.stage === "idle") {
      voice.active = false;
      return 0;
    }

    const amp = advanceEnvelope(voice.ampEnv, preset.ampEnv, dt);
    if (voice.ampEnv.stage === "idle") {
      voice.active = false;
      return 0;
    }
    const mod = advanceEnvelope(voice.modEnv, preset.modEnv, dt);

    const pitchModSemi = advanceNoteFxPitch(voice.noteFx, voice, dt, preset.glideMs);
    voice.lfoPhase += preset.lfo.rateHz * dt;
    const lfo = samplerLfoValue(preset.lfo.wave, voice.lfoPhase) * preset.lfo.depth;
    let lfo2 = 0;
    if (preset.lfo2) {
      voice.lfo2Phase += preset.lfo2.rateHz * dt;
      lfo2 = samplerLfoValue(preset.lfo2.wave, voice.lfo2Phase) * preset.lfo2.depth;
    }
    const lfoU = (lfo + 1) * 0.5;
    const lfo2U = (lfo2 + 1) * 0.5;
    const key = keyTrackOctaves(voice.note);
    const vel = voice.velocity;

    const pitchMod =
      samplerModAmount(preset.routes, "modEnv", "pitch", mod)
      + samplerModAmount(preset.routes, "ampEnv", "pitch", amp)
      + samplerModAmount(preset.routes, "lfo", "pitch", lfoU)
      + samplerModAmount(preset.routes, "lfo2", "pitch", lfo2U)
      + samplerModAmount(preset.routes, "key", "pitch", key, "bipolar")
      + samplerModAmount(preset.routes, "velocity", "pitch", vel)
      + samplerModAmount(preset.routes, "random", "pitch", voice.noteRandom)
      + pitchModSemi;
    const cutoffMod =
      samplerModAmount(preset.routes, "modEnv", "cutoff", mod)
      + samplerModAmount(preset.routes, "ampEnv", "cutoff", amp)
      + samplerModAmount(preset.routes, "lfo", "cutoff", lfoU)
      + samplerModAmount(preset.routes, "lfo2", "cutoff", lfo2U)
      + samplerModAmount(preset.routes, "key", "cutoff", key, "bipolar")
      + voice.noteFx.cutoffOct;
    const ampMod =
      samplerModAmount(preset.routes, "modEnv", "amp", mod)
      + samplerModAmount(preset.routes, "lfo", "amp", lfo, "bipolar")
      + samplerModAmount(preset.routes, "lfo2", "amp", lfo2, "bipolar")
      + samplerModAmount(preset.routes, "key", "amp", key, "bipolar")
      + samplerModAmount(preset.routes, "velocity", "amp", vel);
    const rateMod =
      samplerModAmount(preset.routes, "lfo", "sampleRate", lfoU)
      + samplerModAmount(preset.routes, "lfo2", "sampleRate", lfo2U)
      + samplerModAmount(preset.routes, "modEnv", "sampleRate", mod);

    const loopStartMod =
      samplerModAmount(preset.routes, "lfo", "loopStart", lfoU)
      + samplerModAmount(preset.routes, "lfo2", "loopStart", lfo2U)
      + samplerModAmount(preset.routes, "modEnv", "loopStart", mod);
    const loopEndMod =
      samplerModAmount(preset.routes, "lfo", "loopEnd", lfoU)
      + samplerModAmount(preset.routes, "lfo2", "loopEnd", lfo2U)
      + samplerModAmount(preset.routes, "modEnv", "loopEnd", mod);
    const playbackPosMod =
      samplerModAmount(preset.routes, "lfo", "playbackPos", lfoU)
      + samplerModAmount(preset.routes, "lfo2", "playbackPos", lfo2U)
      + samplerModAmount(preset.routes, "modEnv", "playbackPos", mod)
      + samplerModAmount(preset.routes, "random", "playbackPos", voice.noteRandom);
    const startOffsetCont =
      samplerModAmount(preset.routes, "lfo", "startOffset", lfoU)
      + samplerModAmount(preset.routes, "lfo2", "startOffset", lfo2U);

    const regionSpan = Math.max(1, voice.sampleEnd - voice.baseSampleStart);
    // Continuous startOffset / playbackPos scrub (clamped to region).
    if (Math.abs(playbackPosMod) > 1e-4) {
      const target = voice.baseSampleStart + clamp(playbackPosMod, 0, 1) * regionSpan;
      voice.position += (target - voice.position) * Math.min(1, dt * 40);
    } else if (Math.abs(startOffsetCont) > 1e-4) {
      const target = voice.baseSampleStart + clamp(startOffsetCont, 0, 1) * regionSpan;
      // Soft pull so random/LFO start crackles without hard jumps every sample.
      voice.position += (target - voice.position) * Math.min(1, dt * 8);
    }

    if (voice.loopEnabled) {
      const loopSpan = Math.max(1, voice.baseLoopEnd - voice.baseLoopStart);
      let ls = voice.baseLoopStart + loopStartMod * loopSpan * 0.45;
      let le = voice.baseLoopEnd + loopEndMod * loopSpan * 0.45;
      if (le < ls + 1) {
        const mid = (ls + le) / 2;
        ls = mid - 0.5;
        le = mid + 0.5;
      }
      voice.loopStart = clamp(ls, voice.sampleStart, voice.sampleEnd - 1);
      voice.loopEnd = clamp(le, voice.loopStart + 1, voice.sampleEnd);
    }

    const ref = Math.max(voice.refFreqHz, 1e-3);
    const ratio = (voice.freqHz / ref) * 2 ** (pitchMod / 12) * 2 ** (clamp(rateMod, -2, 2) * 0.5);
    voice.rate = Math.max(1e-6, voice.baseRate * ratio);

    const grainSizeMod =
      samplerModAmount(preset.routes, "lfo", "grainSize", lfoU)
      + samplerModAmount(preset.routes, "lfo2", "grainSize", lfo2U)
      + samplerModAmount(preset.routes, "modEnv", "grainSize", mod);
    const densityMod =
      samplerModAmount(preset.routes, "lfo", "grainDensity", lfoU)
      + samplerModAmount(preset.routes, "modEnv", "grainDensity", mod);
    const sprayMod =
      samplerModAmount(preset.routes, "lfo", "grainSpray", lfoU)
      + samplerModAmount(preset.routes, "random", "grainSpray", voice.noteRandom);
    const stretchMod =
      samplerModAmount(preset.routes, "modEnv", "stretch", mod)
      + samplerModAmount(preset.routes, "lfo", "stretch", lfoU);

    let advanced: { sample: number; done: boolean };
    const preserveDuration =
      preset.playMode === "classic" && preset.preserveDuration === true;
    if (preset.playMode === "granular" || preserveDuration) {
      // Head advances roughly in realtime through the region at stretch=1.
      // Classic+preserveDuration reuses this path so pitched notes keep the
      // sample's wall-clock length (grainy OLA — not a phase vocoder).
      const headRate = voice.sampleRate / Math.max(1, this.sampleRate);
      const grainSizeSec = clamp(
        ((preserveDuration ? Math.max(preset.granular.grainSizeMs, 28) : preset.granular.grainSizeMs)
          / 1000) * (1 + (preserveDuration ? 0 : grainSizeMod)),
        0.005,
        0.5,
      );
      const stretch = preserveDuration
        ? 1
        : clamp(preset.granular.stretch * (1 + stretchMod), 0.01, 8);
      const density = preserveDuration
        ? clamp(Math.max(preset.granular.density, 12), 0.1, 48)
        : clamp(preset.granular.density * (1 + densityMod), 0.1, 48);
      const spray = preserveDuration
        ? 0
        : clamp(preset.granular.spray + sprayMod * 0.5, 0, 1);
      // One-shots must end; looping (or full granular mode) keeps wrapping.
      const wrap = preset.playMode === "granular" || voice.loopEnabled;
      advanced = advanceGranularSample(voice.sample, voice.granular, {
        sampleStart: voice.sampleStart,
        sampleEnd: voice.sampleEnd,
        headRate,
        stretch,
        pitchRate: voice.rate,
        grainSizeSec,
        density,
        spray,
        window: preset.granular.window,
        dt,
        wrap,
      });
      voice.position = voice.granular.head;
      if (advanced.done && !wrap) {
        if (voice.ampEnv.stage !== "release" && voice.ampEnv.stage !== "idle") {
          releaseEnvelope(voice.ampEnv);
          releaseEnvelope(voice.modEnv);
        }
      }
    } else {
      advanced = advanceClassicSample(voice.sample, voice);
      if (advanced.done && !voice.loopEnabled) {
        if (voice.ampEnv.stage !== "release" && voice.ampEnv.stage !== "idle") {
          releaseEnvelope(voice.ampEnv);
          releaseEnvelope(voice.modEnv);
        }
      }
    }

    let sample = advanced.sample;
    const cutoff = preset.filter.cutoffHz * 2 ** cutoffMod;
    sample = applyFilter(
      sample,
      voice,
      preset.filter.type,
      cutoff,
      preset.filter.resonance,
      this.sampleRate,
    );

    voice.velocitySmooth = advanceLinearSmooth(voice.velocitySmooth, voice.velocity, dt);
    const out = sample * amp * voice.velocitySmooth * voice.gain * Math.max(0, 1 + ampMod);
    return Number.isFinite(out) ? out : 0;
  }

  private sf2NoteOn(
    slot: InstrumentSlot,
    patch: Sf2Patch,
    note: number,
    velocity: number,
    noteFx?: VoiceNoteFxParams,
  ): void {
    const bank = this.sf2Banks.get(patch.fontId);
    if (!bank) {
      return;
    }
    const velMidi = Math.round(clamp(velocity, 0, 1) * 127);
    const zone = findSf2Zone(bank, patch.bank, patch.program, Math.round(note), velMidi);
    if (!zone) {
      return;
    }
    const pcm = bank.samples[zone.sampleIndex];
    if (!pcm || pcm.length === 0) {
      return;
    }

    const porta = noteFx?.portaSemiPerSec ?? 0;
    const hardRetrigger = noteFx?.hardRetrigger === true;
    const pool = this.sf2Voices[slot]!;
    const targetHz = midiToHz(note);

    // Legato porta: reuse an active voice, glide pitch, keep sample position.
    if (porta > 0 && !hardRetrigger) {
      const legato = pool.find(v => v.active && !v.releasing);
      if (legato) {
        this.sf2AgeCounter += 1;
        legato.age = this.sf2AgeCounter;
        legato.note = Math.round(note);
        legato.velocity = clamp(velocity, 0, 1);
        legato.velocitySmooth = legato.velocity;
        legato.targetFreqHz = targetHz;
        resetNoteFxFromParams(legato.noteFx, noteFx);
        legato.noteFx.portaSemiPerSec = porta;
        applyPanFromParams(legato, noteFx);
        // Keep legato.panSmooth — mix loop ramps toward legato.pan.
        return;
      }
    }

    // Tracker slots are mono (like synth): a new note cuts previous holds.
    // Chord polyphony is done with multiple tracks, not voices in one slot.
    // C-- relies on this — PatternPlayer does not gate noteOff before the next note.
    let panSeed: number | undefined;
    {
      let newestAge = -1;
      for (const prev of pool) {
        if (!prev.active) {
          continue;
        }
        if (prev.age >= newestAge) {
          newestAge = prev.age;
          panSeed = prev.panSmooth;
        }
      }
    }
    for (const prev of pool) {
      if (!prev.active || prev.releasing) {
        continue;
      }
      prev.releasing = true;
      prev.releaseEnv = 1;
      if (prev.loopMode === 3) {
        prev.loopMode = 0;
      }
    }

    let voice = pool.find(v => !v.active);
    if (!voice) {
      voice = pool.find(v => v.releasing)
        ?? pool.reduce((oldest, v) => (v.age < oldest.age ? v : oldest));
    }
    this.sf2AgeCounter += 1;
    const sampleEnd = Math.min(zone.sampleEnd, pcm.length);
    const sampleStart = Math.max(0, Math.min(zone.sampleStart, sampleEnd - 1));
    const pitchRatio = 2 ** ((note - zone.rootKey) / 12 + zone.tuneCents / 1200);
    const baseRate = (zone.sampleRate / Math.max(1, this.sampleRate)) * pitchRatio;
    voice.active = true;
    voice.age = this.sf2AgeCounter;
    voice.note = Math.round(note);
    voice.velocity = clamp(velocity, 0, 1);
    voice.velocitySmooth = voice.velocity;
    voice.sample = pcm;
    voice.position = sampleStart;
    voice.baseRate = baseRate;
    voice.rate = baseRate;
    voice.sampleStart = sampleStart;
    voice.sampleEnd = sampleEnd;
    voice.loopStart = Math.max(sampleStart, Math.min(zone.loopStart, sampleEnd - 1));
    voice.loopEnd = Math.max(voice.loopStart + 1, Math.min(zone.loopEnd, sampleEnd));
    voice.loopMode = zone.loopMode;
    voice.gain = clamp(zone.gain * patch.gain, 0, 1);
    voice.releasing = false;
    voice.releaseEnv = 1;
    voice.filterZ1 = 0;
    voice.refFreqHz = targetHz;
    voice.freqHz = targetHz;
    voice.targetFreqHz = targetHz;
    resetNoteFxFromParams(voice.noteFx, noteFx);
    applyVoicePanTarget(voice, noteFx, this.slotMixes[slot]?.pan ?? 0, panSeed);
  }

  private sf2NoteOff(slot: InstrumentSlot, note?: number): void {
    const target = note === undefined ? undefined : Math.round(note);
    for (const voice of this.sf2Voices[slot] ?? []) {
      if (!voice.active || voice.releasing) {
        continue;
      }
      if (target !== undefined && voice.note !== target) {
        continue;
      }
      // Continuous loop (1): keep looping until release finishes.
      // Loop-until-release (3): stop looping and play through, with release ramp.
      voice.releasing = true;
      voice.releaseEnv = 1;
      if (voice.loopMode === 3) {
        voice.loopMode = 0;
      }
    }
  }

  private renderSf2Voice(voice: Sf2Voice, dt: number): number {
    const pcm = voice.sample;
    if (!pcm.length || voice.position >= voice.sampleEnd) {
      voice.active = false;
      return 0;
    }

    const pitchModSemi = advanceNoteFxPitch(voice.noteFx, voice, dt, undefined);
    const ref = Math.max(voice.refFreqHz, 1e-3);
    const ratio = (voice.freqHz / ref) * 2 ** (pitchModSemi / 12);
    voice.rate = voice.baseRate * ratio;

    const i0 = Math.floor(voice.position);
    const i1 = Math.min(i0 + 1, voice.sampleEnd - 1);
    const frac = voice.position - i0;
    const s0 = pcm[i0] ?? 0;
    const s1 = pcm[i1] ?? s0;
    let sample = s0 + (s1 - s0) * frac;

    voice.position += voice.rate;

    const canLoop = voice.loopMode === 1 || voice.loopMode === 3;
    if (canLoop && voice.loopEnd > voice.loopStart + 1) {
      while (voice.position >= voice.loopEnd) {
        voice.position -= voice.loopEnd - voice.loopStart;
      }
    } else if (voice.position >= voice.sampleEnd) {
      voice.active = false;
    }

    if (voice.releasing) {
      voice.releaseEnv -= dt / SF2_RELEASE_SEC;
      if (voice.releaseEnv <= 0) {
        voice.active = false;
        voice.releasing = false;
        return 0;
      }
      sample *= voice.releaseEnv;
    }

    const cutoff = clamp(SF2_FILTER_BASE_HZ * 2 ** voice.noteFx.cutoffOct, 200, 18000);
    const rc = 1 / (2 * Math.PI * cutoff);
    const alpha = dt / (rc + dt);
    voice.filterZ1 += alpha * (sample - voice.filterZ1);
    sample = voice.filterZ1;

    voice.velocitySmooth = advanceLinearSmooth(voice.velocitySmooth, voice.velocity, dt);
    const out = sample * voice.velocitySmooth * voice.gain;
    return Number.isFinite(out) ? out : 0;
  }

  private drumNoteOn(
    slot: InstrumentSlot,
    kit: DrumKit,
    note: number,
    velocity: number,
    noteFx?: VoiceNoteFxParams,
  ): void {
    const padIndex = drumPadIndexForNote(kit, note);
    if (padIndex === undefined) {
      return;
    }
    const pad = kit.pads[padIndex]!;
    const sampleMode = normalizeDrumKitMode(kit.mode) === "sample";
    let sample: SamplerRuntimeSample | undefined;
    if (sampleMode) {
      const sampleId = pad.sampleId?.trim();
      if (!sampleId) {
        return;
      }
      sample = this.samplerSamples.get(sampleId);
      if (!sample?.pcm.length) {
        return;
      }
    }

    const pool = this.drumVoices[slot]!;
    const porta = noteFx?.portaSemiPerSec ?? 0;
    const donor = porta > 0 ? newestActiveVoice(pool) : undefined;

    // Sample drums are mono per track (like sampler / SF2). PatternPlayer does
    // not gate noteOff before the next pitched note for C--, so a new hit must
    // release priors — otherwise overlapping oneshots stack.
    let panSeed: number | undefined;
    if (sampleMode) {
      let newestAge = -1;
      for (const prev of pool) {
        if (!prev.active || prev.playMode !== "sample") {
          continue;
        }
        if (prev.age >= newestAge) {
          newestAge = prev.age;
          panSeed = prev.panSmooth;
        }
      }
      for (const prev of pool) {
        if (!prev.active || prev.playMode !== "sample") {
          continue;
        }
        if (prev.ampEnv.stage !== "release" && prev.ampEnv.stage !== "idle") {
          releaseEnvelope(prev.ampEnv);
        }
        prev.velocity = 0;
      }
    }

    let voice = pool.find(v => !v.active);
    if (!voice) {
      voice = sampleMode
        ? (pool.find(v => v.ampEnv.stage === "release")
          ?? pool.reduce((oldest, v) => (v.age < oldest.age ? v : oldest)))
        : pool.reduce((oldest, v) => (v.age < oldest.age ? v : oldest));
    }
    this.drumAgeCounter += 1;
    voice.active = true;
    voice.age = this.drumAgeCounter;
    voice.note = note;
    voice.velocity = clamp(velocity, 0, 1);
    voice.velocitySmooth = voice.velocity;
    voice.pad = {
      ...pad,
      ...(pad.region ? { region: { ...pad.region } } : {}),
      ampEnv: { ...(pad.ampEnv ?? DEFAULT_DRUM_SAMPLE_AMP_ENV) },
    };
    voice.phase = 0;
    voice.noiseState = 1;
    voice.filterZ1 = 0;
    voice.timeSec = 0;
    voice.refFreqHz = Math.max(pad.pitchHz, 1e-3);
    voice.targetFreqHz = voice.refFreqHz;
    // Porta: seed from newest active sibling so the hit glides into pad pitch.
    voice.freqHz = donor && donor !== voice ? donor.freqHz : voice.refFreqHz;
    resetNoteFxFromParams(voice.noteFx, noteFx);
    if (porta > 0) {
      voice.noteFx.portaSemiPerSec = porta;
    }
    applyVoicePanTarget(voice, noteFx, this.slotMixes[slot]?.pan ?? 0, panSeed);

    if (sampleMode && sample) {
      voice.playMode = "sample";
      voice.sampleId = sample.id;
      voice.sample = sample.pcm;
      voice.sampleRate = sample.sampleRate;
      const pcmLen = sample.pcm.length;
      let start = pad.region ? Math.floor(pad.region.start) : 0;
      let end = pad.region ? Math.floor(pad.region.end) : pcmLen;
      start = clamp(start, 0, Math.max(0, pcmLen - 1));
      end = clamp(end, start + 1, pcmLen);
      voice.sampleStart = start;
      voice.sampleEnd = end;
      voice.position = start;
      const pitchSemis = pad.pitchSemis ?? 0;
      const root = sample.rootKey ?? 60;
      // Keep pad pitch relative — rootKey used only as reference when present;
      // oneshot pads play at pitchSemis with no melodic remap by MIDI note.
      void root;
      voice.playbackRate =
        (sample.sampleRate / Math.max(1, this.sampleRate)) * 2 ** (pitchSemis / 12);
      triggerEnvelope(voice.ampEnv);
      return;
    }

    voice.playMode = "synth";
    voice.sampleId = "";
    voice.sample = null;
    voice.sampleStart = 0;
    voice.sampleEnd = 0;
    voice.position = 0;
    voice.playbackRate = 1;
    voice.ampEnv.stage = "idle";
    voice.ampEnv.level = 0;
  }

  private renderDrumVoice(voice: DrumVoice, dt: number): number {
    const pad = voice.pad;

    if (voice.playMode === "sample") {
      const adsr = pad.ampEnv ?? DEFAULT_DRUM_SAMPLE_AMP_ENV;
      const amp = advanceEnvelope(voice.ampEnv, adsr, dt);
      if (voice.ampEnv.stage === "idle") {
        voice.active = false;
        return 0;
      }
      const pcm = voice.sample;
      if (!pcm || pcm.length === 0 || voice.position >= voice.sampleEnd) {
        voice.active = false;
        return 0;
      }
      voice.velocitySmooth = advanceLinearSmooth(voice.velocitySmooth, voice.velocity, dt);
      const pitchModSemi = advanceNoteFxPitch(voice.noteFx, voice, dt, undefined);
      const rate = voice.playbackRate * 2 ** (pitchModSemi / 12);
      const pos = voice.position;
      const i0 = Math.floor(pos);
      const i1 = Math.min(i0 + 1, voice.sampleEnd - 1);
      const frac = pos - i0;
      const s0 = pcm[i0] ?? 0;
      const s1 = pcm[i1] ?? s0;
      const sample = s0 + (s1 - s0) * frac;
      voice.position += rate;
      if (voice.position >= voice.sampleEnd) {
        voice.active = false;
      }
      const out = sample * amp * pad.level * voice.velocitySmooth;
      return Number.isFinite(out) ? out : 0;
    }

    const decaySec = Math.max(0.01, pad.decayMs / 1000);
    voice.timeSec += dt;
    if (voice.timeSec >= decaySec * 1.35) {
      voice.active = false;
      return 0;
    }

    voice.velocitySmooth = advanceLinearSmooth(voice.velocitySmooth, voice.velocity, dt);
    const ampEnv = Math.exp(-4.5 * (voice.timeSec / decaySec));

    const pitchModSemi = advanceNoteFxPitch(voice.noteFx, voice, dt, undefined);

    const t = voice.timeSec;
    const pitchEnv = Math.exp(-8 * (t / Math.max(0.02, decaySec * 0.45)));
    const freq = voice.freqHz * 2 ** (pad.pitchEnv * pitchEnv + pitchModSemi / 12);
    const inc = freq / this.sampleRate;
    voice.phase += inc;

    const tone = Math.sin(2 * Math.PI * (voice.phase % 1)) * pad.toneLevel;
    this.noiseScratch.value = voice.noiseState;
    const noise = noiseSample(this.noiseScratch) * pad.noiseLevel;
    voice.noiseState = this.noiseScratch.value;
    const click = (t < 0.004 ? (1 - t / 0.004) : 0) * pad.clickLevel
      * (noiseSample(this.noiseScratch) * 0.5 + 0.5);

    let mixed = tone + noise + click;
    const cutoff = clamp(pad.filterHz * 2 ** voice.noteFx.cutoffOct, 200, 18000);
    const rc = 1 / (2 * Math.PI * cutoff);
    const alpha = dt / (rc + dt);
    voice.filterZ1 += alpha * (mixed - voice.filterZ1);
    mixed = voice.filterZ1;

    const out = mixed * ampEnv * pad.level * voice.velocitySmooth;
    return Number.isFinite(out) ? out : 0;
  }

  private renderVoice(slot: InstrumentSlot, dt: number): number {
    const voice = this.voices[slot];
    const preset = this.presets[slot];
    if (!voice || !preset || voice.ampEnv.stage === "idle") {
      if (voice) {
        voice.active = false;
      }
      return 0;
    }

    const amp = advanceEnvelope(voice.ampEnv, preset.ampEnv, dt);
    const mod = advanceEnvelope(voice.modEnv, preset.modEnv, dt);

    const vibratoSemi = advanceNoteFxPitch(
      voice.noteFx,
      voice,
      dt,
      preset.glideMs,
    );

    voice.lfoPhase += preset.lfo.rateHz * dt;
    const lfoWave = preset.lfo.wave === "tri"
      ? 1 - 4 * Math.abs((voice.lfoPhase % 1) - 0.5)
      : Math.sin(2 * Math.PI * (voice.lfoPhase % 1));
    const lfo = lfoWave * preset.lfo.depth;
    const key = keyTrackOctaves(voice.note);

    const pitchMod =
      modAmount(preset.routes, "modEnv", "pitch", mod)
      + modAmount(preset.routes, "ampEnv", "pitch", amp)
      + modAmount(preset.routes, "lfo", "pitch", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "pitch", key, "bipolar")
      + vibratoSemi;
    const cutoffMod =
      modAmount(preset.routes, "modEnv", "cutoff", mod)
      + modAmount(preset.routes, "ampEnv", "cutoff", amp)
      + modAmount(preset.routes, "lfo", "cutoff", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "cutoff", key, "bipolar")
      + voice.noteFx.cutoffOct;
    const ampMod =
      modAmount(preset.routes, "modEnv", "amp", mod)
      + modAmount(preset.routes, "lfo", "amp", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "amp", key, "bipolar");
    const pwmMod =
      modAmount(preset.routes, "modEnv", "pwm", mod)
      + modAmount(preset.routes, "lfo", "pwm", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "pwm", key, "bipolar");
    const fmDepthMod =
      modAmount(preset.routes, "modEnv", "fmDepth", mod)
      + modAmount(preset.routes, "ampEnv", "fmDepth", amp)
      + modAmount(preset.routes, "lfo", "fmDepth", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "fmDepth", key, "bipolar");
    const amDepthMod =
      modAmount(preset.routes, "modEnv", "amDepth", mod)
      + modAmount(preset.routes, "ampEnv", "amDepth", amp)
      + modAmount(preset.routes, "lfo", "amDepth", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "amDepth", key, "bipolar");

    const freq = voice.freqHz * 2 ** (pitchMod / 12);

    const o1 = preset.osc1;
    const o2 = preset.osc2;
    const o3 = preset.osc3;
    const f1 = freq * 2 ** (o1.octave + o1.semis / 12) * 2 ** (o1.detuneCents / 1200);
    const f2 = freq * 2 ** (o2.octave + o2.semis / 12) * 2 ** (o2.detuneCents / 1200);
    const f3 = freq * 2 ** (o3.octave + o3.semis / 12) * 2 ** (o3.detuneCents / 1200);

    const inc1 = f1 / this.sampleRate;
    const inc2 = f2 / this.sampleRate;
    const inc3 = f3 / this.sampleRate;
    voice.osc1Phase += inc1;
    voice.osc2Phase += inc2;
    voice.osc3Phase += inc3;

    const pwm1 = clamp((o1.pwm ?? 0.5) + pwmMod * 0.1, 0.05, 0.95);
    const pwm2 = clamp((o2.pwm ?? 0.5) + pwmMod * 0.1, 0.05, 0.95);
    const pwm3 = clamp((o3.pwm ?? 0.5) + pwmMod * 0.1, 0.05, 0.95);

    this.noiseScratch.value = voice.noiseState;

    // Osc3 is the modulator, so it is read *before* the carriers and at full
    // amplitude — its `level` governs only how much of it is heard, letting
    // level 0 give a pure modulator.
    const s3 = oscSample(o3.wave, voice.osc3Phase, inc3, pwm3, this.noiseScratch);

    const fm = preset.fm;
    const velScale = 1 - fm.velocityToFm + fm.velocityToFm * voice.velocity;
    const fmDepth = clamp((fm.fmDepth + fmDepthMod) * velScale, 0, MAX_FM_DEPTH);
    const amDepth = clamp(fm.amDepth + amDepthMod, 0, 1);
    const modulates1 = fm.target === "osc1" || fm.target === "both";
    const modulates2 = fm.target === "osc2" || fm.target === "both";
    // Phase modulation: offset the phase we *read*, never the accumulator, so
    // DC in the modulator cannot integrate into pitch drift the way true FM does.
    const phaseOffset = fmDepth * s3;
    const off1 = modulates1 ? phaseOffset : 0;
    const off2 = modulates2 ? phaseOffset : 0;

    let s1 = oscSample(o1.wave, voice.osc1Phase + off1, inc1, pwm1, this.noiseScratch) * o1.level;
    let s2 = oscSample(o2.wave, voice.osc2Phase + off2, inc2, pwm2, this.noiseScratch) * o2.level;
    if (modulates1) {
      s1 = ringMod(s1, s3, amDepth);
    }
    if (modulates2) {
      s2 = ringMod(s2, s3, amDepth);
    }

    const noise = preset.noise.level > 0
      ? noiseSample(this.noiseScratch) * preset.noise.level
      : 0;
    voice.noiseState = this.noiseScratch.value;

    let mixed = s1 + s2 + s3 * o3.level + noise;
    const cutoff = preset.filter.cutoffHz * 2 ** cutoffMod;
    mixed = applyFilter(mixed, voice, preset.filter.type, cutoff, preset.filter.resonance, this.sampleRate);

    voice.velocitySmooth = advanceLinearSmooth(voice.velocitySmooth, voice.velocity, dt);
    const gain = amp * voice.velocitySmooth * (1 + ampMod);
    const out = mixed * gain;
    // Inf filter state * near-zero amp → NaN; never emit that into master FX.
    if (!Number.isFinite(out)) {
      voice.filterZ1 = 0;
      voice.filterZ2 = 0;
      return 0;
    }
    return out;
  }

  /** Test helper — current FX stack snapshot. */
  getFxStack(): readonly FxNode[] {
    return this.fx.nodes;
  }

  getBpm(): number {
    return this.bpm;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  getTransportStartTime(): number {
    return this.transportStartTime;
  }
}

export function createTsAudioKernel(): AudioKernel {
  return new TsAudioKernel();
}
